<template>
    <div class="font-inter">
        <div class="space-between mt-8 flex w-full px-8">
            <div class="flex-1">
                <div
                    v-if="mapping?.file?.ref_number"
                    class="text-xl"
                >
                    File: {{ mapping.file.ref_number }}
                </div>
                <h2
                    v-if="identityVerificationResult?.is_self_serve"
                    class="text-lg font-normal"
                >
                    In-person identity verification
                </h2>
                <h2
                    v-else
                    class="text-lg font-normal"
                >
                    Identity Verification Details
                </h2>
            </div>
            <div class="vertical-center mt-4 flex-initial text-right">
                <h1 :class="statusClass">{{ statusString }}</h1>
            </div>
        </div>
        <Divider />
        <div
            v-if="reportState === 'loaded' && identityVerificationResult && mapping"
            class="flex w-full flex-col items-start gap-4 px-8"
        >
            <HeaderReportBanner />
            <OverallResult
                :result="identityVerificationResult.pdfMapping.overallResult"
                :selfie-enabled="identityVerificationResult.pdfMapping.selfieVerification.selfieAnalysisEnabled"
            />
            <VerificationSection
                :helperText="IDV_HELPER_KYC_CHECK"
                omit-error-list
                :sectionCheckResult="mapping.overallResult.knownYourCustomer"
                title="Know Your Customer (KYC)"
            >
                <KYCReport
                    :country
                    :known-your-customer="mapping.knownYourCustomer"
                />
            </VerificationSection>
            <VerificationSection
                :helperText="IDV_HELPER_GOVERNMENT_ID_ANALYSIS"
                omit-error-list
                :sectionCheckResult="mapping.overallResult.govIdAnalysis"
                title="Government ID Analysis"
            >
                <GovernmentIdReport
                    class="mt-4"
                    :country
                    :gov-id-analysis="mapping.govIdAnalysis"
                />
            </VerificationSection>
            <VerificationSection
                v-if="mapping.selfieVerification.selfieAnalysisEnabled"
                :helperText="IDV_HELPER_SELFIE_VERIFICATION"
                omit-error-list
                :sectionCheckResult="mapping.overallResult.selfieVerification"
                title="Selfie Verification"
            >
                <SelfieReport
                    :selfie-analysis="mapping.selfieVerification"
                    :selfie-overall-result="mapping.overallResult.selfieVerification"
                />
            </VerificationSection>
            <VerificationSection
                :helperText="IDV_HELPER_IP_ADDRESS_INFORMATION"
                :sectionCheckResult="STATIC_REVIEW_RESULTS"
                title="IP Address information"
            >
                <IPAddressReport
                    v-if="mapping.ipAddressResult"
                    :ip-analysis="mapping.ipAddressResult"
                />
                <div
                    v-else
                    class="flex flex-col items-center justify-center p-4"
                >
                    <DsIcon
                        class="mb-4"
                        icon="fa-regular fa-user-viewfinder"
                        size="lg"
                    />
                    <h3 class="mb-1 text-content-lg font-medium">IP Address not available</h3>
                </div>
            </VerificationSection>
            <VerificationSection
                :helperText="`Additional Information allows you to enrich your understanding of the subject using key
             identifiers like email addresses, phone numbers and online presence pulled from authoritative 
             data sources.`"
                :sectionCheckResult="STATIC_REVIEW_RESULTS"
                title="Additional information"
            >
                <AdditionalInformationReport :additional-information="mapping.additionalInformation" />
            </VerificationSection>
        </div>
        <div
            v-if="reportState === 'loaded'"
            class="flex w-full flex-col gap-2"
        >
            <Divider />
            <div class="flex flex-row items-end justify-end gap-1 px-8 text-sm">
                <DsButton
                    :download="`identity-verification-${props.id}.pdf`"
                    :href="`/user/id-verification/${props.id}/download`"
                    leading="fas fa-file-pdf"
                    size="md"
                    target="_blank"
                    variant="primary"
                >
                    Download report
                </DsButton>
            </div>
        </div>
        <div v-if="reportState === 'loading'">
            <ModalLoader>
                <template #loadingText>
                    <div class="text-base text-ds-primary-500">Loading Identity verification request</div>
                </template>
            </ModalLoader>
        </div>
        <div v-if="reportState === 'failure'">
            <IDVReportLoadingFailure
                @close="$emit('close')"
                @try-reload="loadReport(props.id)"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import type { CheckResult, IdentityVerificationResult } from '@/api/interfaces/identity-verification-report.api';
import { CheckResultState } from '@/api/interfaces/identity-verification-report.api';
import ModalLoader from '@/components/Components/ModalLoader.vue';
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import { useIdentityVerificationReportService } from '@/services/identity-verification-report.service';
import Divider from 'primevue/divider';
import { computed, onMounted, ref, watch } from 'vue';
import HeaderReportBanner from './common/HeaderReportBanner.vue';
import VerificationSection from './common/VerificationSection.vue';
import {
    IDV_HELPER_GOVERNMENT_ID_ANALYSIS,
    IDV_HELPER_IP_ADDRESS_INFORMATION,
    IDV_HELPER_KYC_CHECK,
    IDV_HELPER_SELFIE_VERIFICATION,
} from './idv-static-helper-text';
import IDVReportLoadingFailure from './IDVReportLoadingFailure.vue';

import DsIcon from '@ds/DsIcon.vue';
import AdditionalInformationReport from './reports/AdditionalInformationReport.vue';
import GovernmentIdReport from './reports/GovernmentIdReport.vue';
import IPAddressReport from './reports/IPAddressReport.vue';
import KYCReport from './reports/KYCReport.vue';
import OverallResult from './reports/OverallResult.vue';
import SelfieReport from './reports/SelfieReport.vue';

const props = defineProps<{
    id: IdentityVerificationResult['id'];
}>();

defineEmits(['close']);

const STATIC_REVIEW_RESULTS: CheckResult = {
    state: CheckResultState.NotApplicable,
    stateText: 'Review Results',
    text: 'Review Results',
    infoList: [],
};

const identityVerificationResult = ref<IdentityVerificationResult | undefined>();

const reportState = ref<'loading' | 'failure' | 'loaded'>('loading');

const idvReportService = useIdentityVerificationReportService();

async function loadReport(id: IdentityVerificationResult['id']) {
    reportState.value = 'loading';
    try {
        identityVerificationResult.value = (await idvReportService.loadIdvReport(id)) ?? undefined;
    } catch (error) {
        reportState.value = 'failure';
    } finally {
        reportState.value = reportState.value === 'loading' ? 'loaded' : reportState.value;
    }
}

onMounted(async () => {
    loadReport(props.id);
});

watch(
    () => props.id,
    id => {
        loadReport(id);
    },
);

const mutatedReport = computed(() =>
    identityVerificationResult?.value ? idvReportService.mutateReport(identityVerificationResult.value) : null,
);

const statusString = computed(() => mutatedReport.value?.status);

const statusClass = computed(() => {
    const rawStatus = identityVerificationResult.value?.raw_status;
    if (rawStatus === 'success' || rawStatus === 'amended') {
        return 'text-ds-green-600';
    } else if (rawStatus === 'overridden') {
        return 'text-content-secondary';
    } else {
        return 'text-semantic-destructive-primary-default';
    }
});
const mapping = computed(() => mutatedReport.value?.pdfMapping);
const country = computed(() => mutatedReport.value?.reports?.selected_country_code || 'US');
</script>
